import { useState, useEffect, useRef, useCallback } from "react";
import { Rabbit } from "lucide-react";

interface DragButtonProps {
  isHidden?: boolean;
}

export function DragButton({ isHidden = false }: DragButtonProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const touchStartY = useRef<number | null>(null);
  const shouldTrackTouch = useRef(false);
  const isTouching = useRef(false);
  const baselineViewportHeight = useRef<number | null>(null);

  const updatePositionFromViewport = useCallback(() => {
    if (baselineViewportHeight.current === null) return;
    
    const currentViewportHeight = window.innerHeight;
    
    if (currentViewportHeight > baselineViewportHeight.current) {
      setDragY(-30);
    } else {
      setDragY(0);
    }
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile && baselineViewportHeight.current === null) {
      baselineViewportHeight.current = window.innerHeight;
    }
  }, [isMobile]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsVisible(false);
      } else {
        updatePositionFromViewport();
        
        const timer = setTimeout(() => {
          setIsVisible(true);
        }, 3000);
        
        return () => clearTimeout(timer);
      }
    };

    const initialTimer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updatePositionFromViewport]);

  useEffect(() => {
    if (!isMobile) return;
    
    const handleResize = () => {
      if (!isTouching.current) {
        updatePositionFromViewport();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, updatePositionFromViewport]);

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const mainContentFrame = target.closest('[class*="overflow-y-auto"]');
      
      if (!mainContentFrame) {
        // Background scroll - always track
        touchStartY.current = e.touches[0].clientY;
        shouldTrackTouch.current = true;
        isTouching.current = true;
      } else {
        // Check if the content is actually scrollable
        const isScrollable = mainContentFrame.scrollHeight > mainContentFrame.clientHeight;
        
        if (isScrollable) {
          // Content needs scrolling - don't track (user navigating content)
          shouldTrackTouch.current = false;
          isTouching.current = true;
        } else {
          // Content fits in viewport - track background scroll
          touchStartY.current = e.touches[0].clientY;
          shouldTrackTouch.current = true;
          isTouching.current = true;
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!shouldTrackTouch.current || touchStartY.current === null) return;
      
      const currentTouchY = e.touches[0].clientY;
      const deltaY = currentTouchY - touchStartY.current;
      
      if (deltaY < 0) {
        setDragY(-30);
      } else if (deltaY > 0) {
        setDragY(0);
      }
    };

    const handleTouchEnd = () => {
      touchStartY.current = null;
      shouldTrackTouch.current = false;
      
      updatePositionFromViewport();
      
      isTouching.current = false;
    };

    if (isMobile) {
      window.addEventListener('touchstart', handleTouchStart, { passive: true });
      window.addEventListener('touchmove', handleTouchMove, { passive: true });
      window.addEventListener('touchend', handleTouchEnd);

      return () => {
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isMobile, updatePositionFromViewport]);

  return (
    <>
      {/* Vertical line track */}
      <div 
        className={`fixed right-[19px] z-[49] md:hidden w-px bg-white/30 transition-opacity ${isHidden || !isVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ bottom: '29px', height: '14px' }}
        aria-hidden="true"
      />
      
      {/* Scroll indicator */}
      <div
        className={`fixed bottom-3 right-3 z-[50] md:hidden transition-all ${isHidden || !isVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ 
          transform: `translateY(${dragY}px)`
        }}
        aria-hidden="true"
      >
        <Rabbit
          className="w-4.5 h-4.5 text-[var(--accent-blue)] opacity-70"
          strokeWidth={2}
        />
      </div>
    </>
  );
}